<template>
  <div>
    <v-row class="mb-2" dense>
      <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" sm="3">
        <v-card outlined class="pa-3 kpi-card">
          <div class="kpi-label">{{ kpi.label }}</div>
          <div class="kpi-value">{{ kpi.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="payments"
        :loading="loading"
        :loading-text="$t('finance_loading_payments')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_payment_items')"
        @click:row="openPayment"
      >
        <template #item.document="{ item }">
          <span class="font-weight-bold">{{ item.document }}</span>
        </template>
        <template #item.amount="{ item }">{{ formatCurrency(item.amount) }}</template>
        <template #item.dueDate="{ item }">{{ localeDate(item.dueDate) }}</template>
        <template #item.status="{ item }">
          <v-chip small :color="PLATFORM_PAYMENT_STATUS_META[item.status].color" dark>
            {{ $t(PLATFORM_PAYMENT_STATUS_META[item.status].labelKey) }}
          </v-chip>
        </template>
      </v-data-table>
      <p v-if="!loading && payments.length > 0" class="hint-copy px-3 pb-3 mb-0">
        {{ $t("finance_click_document_payment_hint") }}
      </p>
    </v-card>

    <platform-payment-detail-dialog
      v-model="detailOpen"
      :payment="selectedPayment"
      :saving="saving"
      @confirm-payment="confirmPayment"
      @link-bank="mockAction($t('finance_link_bank'))"
      @upload-proof="onUploadProof"
    />
  </div>
</template>

<script>
import gpsApi from "@/plugins/gpsApi";
import hasSnackbar from "@/mixins/hasSnackbar";
import PlatformPaymentDetailDialog from "./dialogs/PlatformPaymentDetailDialog.vue";

export const PLATFORM_PAYMENT_STATUS_META = {
  paid: { labelKey: "finance_status_platform_payment_paid", color: "success" },
  due: { labelKey: "finance_status_platform_payment_due", color: "error" },
  "not-due": { labelKey: "finance_status_platform_payment_not_due", color: "warning" },
};

const mapPlatformPaymentDto = (dto) => ({
  id: dto.id,
  document: dto.document,
  amount: Number(dto.amount) || 0,
  dueDate: dto.due_date,
  status: dto.status,
  iban: dto.iban,
  model: dto.model,
  referenceNumber: dto.reference_number,
});

export default {
  name: "FinanceTabPlatformPayments",

  components: { PlatformPaymentDetailDialog },

  mixins: [hasSnackbar],

  data: () => ({
    PLATFORM_PAYMENT_STATUS_META,
    payments: [],
    loading: true,
    saving: false,
    detailOpen: false,
    selectedPayment: null,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    headers() {
      return [
        { text: this.$t("finance_document"), value: "document" },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
        { text: this.$t("finance_due_date"), value: "dueDate" },
        { text: this.$t("finance_status_label"), value: "status" },
      ];
    },

    kpis() {
      const due = this.payments.filter((p) => p.status === "due");
      const notDue = this.payments.filter((p) => p.status === "not-due");
      const paid = this.payments.filter((p) => p.status === "paid");
      const sum = (list) => list.reduce((s, p) => s + p.amount, 0);

      return [
        { label: this.$t("finance_total_debt"), value: this.formatCurrency(sum(due) + sum(notDue)) },
        { label: this.$t("finance_status_platform_payment_due"), value: this.formatCurrency(sum(due)) },
        { label: this.$t("finance_status_platform_payment_not_due"), value: this.formatCurrency(sum(notDue)) },
        { label: this.$t("finance_status_platform_payment_paid"), value: this.formatCurrency(sum(paid)) },
      ];
    },
  },

  created() {
    this.loadPayments();
  },

  methods: {
    async loadPayments() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await gpsApi.get(
          `/restaurants/${this.restaurantId}/platform-payments`
        );
        this.payments = (data?.data || []).map(mapPlatformPaymentDto);
      } catch (error) {
        this.showErrorSnackbar(
          error?.response?.data?.message || this.$t("finance_platform_payments_load_error")
        );
      } finally {
        this.loading = false;
      }
    },

    openPayment(payment) {
      this.selectedPayment = payment;
      this.detailOpen = true;
    },

    async confirmPayment(payment) {
      this.saving = true;
      try {
        const { data } = await gpsApi.put(`/platform-payments/${payment.id}`, {
          status: "paid",
        });
        const updated = mapPlatformPaymentDto(data);
        this.payments = this.payments.map((p) => (p.id === updated.id ? updated : p));
        if (this.selectedPayment?.id === updated.id) {
          this.selectedPayment = updated;
        }
        this.showSuccessSnackbar(this.$t("finance_payment_confirmed_pending_review"));
      } catch (error) {
        this.showErrorSnackbar(
          error?.response?.data?.message || this.$t("finance_confirm_payment_error")
        );
      } finally {
        this.saving = false;
      }
    },

    onUploadProof(file) {
      this.showInfoSnackbar(this.$t("finance_proof_ready_notice", { file: file.name }));
    },

    mockAction(action) {
      this.showInfoSnackbar(this.$t("finance_action_not_connected", { action }));
    },

    formatCurrency(amount) {
      const value = Number(amount) || 0;
      return `${value.toFixed(2)} ${this.restaurantCurrency}`;
    },
  },
};
</script>

<style scoped>
.kpi-card {
  border-radius: 12px;
}

.kpi-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
}

.kpi-value {
  font-size: 1.15rem;
  font-weight: 700;
  margin-top: 4px;
}

.hint-copy {
  font-size: 0.78rem;
  color: #9aa4b2;
}

::v-deep tbody tr {
  cursor: pointer;
}
</style>
